import { ActionsBar, Section, Spinner } from '@ydistri/ds';
import React, { useCallback, useMemo, useState } from 'react';
import CreateIconButton from '../../../../components/buttons/CreateIconButton';
import { useSelector } from 'react-redux';
import {
  getArchivedStateSorter,
  lastUpdatedAtSorter,
  nameSorter,
  onlyActive,
} from '../../common/administrationItemsLib';
import { ReduxState } from '../../../../store';
import useAdministrationEntityLists from '../useAdministrationEntityLists';
import {
  EntityListTypeSupported,
  getEntityListSpecification,
  getEntityListsSummary,
} from '../entityListsLib';
import EntityListsList from './EntityListsList';
import EntityListModal from '../modals/EntityListModal';
import EntityListsFilters from './components/EntityListsFilters';
import { EntityListResponse } from '@ydistri/api-sdk';
import { Alert } from 'antd';

interface EntityListCatalogProps {
  entityListType: EntityListTypeSupported;
}

const EntityListCatalog: React.FC<EntityListCatalogProps> = ({ entityListType }) => {
  const showArchivedItems = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].showArchivedItems,
  );
  const sortField = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].sortField,
  );

  const sourceTypeFilters = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].sourceTypeFilters,
  );

  const { data: entityLists, isFetching } = useAdministrationEntityLists(entityListType);

  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );

  const [modalOpened, setModalOpened] = useState<boolean>(false);

  const onCreateEntityListClicked = useCallback(() => {
    setModalOpened(previousModalOpened => !previousModalOpened);
  }, []);

  const onCreateEntityListModalClose = useCallback(() => {
    setModalOpened(false);
  }, []);

  //Archived entity lists are not displayed by default
  //If they are, they are listed after the active ones and sorted by name
  const displayedData = useMemo(() => {
    let result: EntityListResponse[] = [];
    if (entityLists) {
      const sorter = sortField === 'LastUpdatedAt' ? lastUpdatedAtSorter : nameSorter;

      const requestedSourceType = entityLists.filter(el => {
        if (sourceTypeFilters && sourceTypeFilters.length > 0) {
          return sourceTypeFilters.includes(el.entityListSourceTypeId);
        } else {
          return false;
        }
      });

      if (!showArchivedItems) {
        result = onlyActive(requestedSourceType).sort(sorter);
      } else {
        result = Array.from(requestedSourceType).sort(getArchivedStateSorter(sorter));
      }
    }
    return result;
  }, [entityLists, sortField, showArchivedItems, sourceTypeFilters]);

  const headerActions = useMemo(() => {
    return (
      <ActionsBar>
        <CreateIconButton
          size="small"
          tooltip="Create list"
          onClick={onCreateEntityListClicked}
          data-testid="create_new_entity_list"
        />
      </ActionsBar>
    );
  }, [onCreateEntityListClicked]);

  const listContent = useMemo(() => {
    if (isFetching) {
      return <Spinner />;
    }

    if (displayedData.length > 0) {
      return (
        <EntityListsList
          entityListType={entityListType}
          selectedItem={selectedEntityList}
          data={displayedData}
        />
      );
    }

    if (entityLists && entityLists.length > 0 && displayedData.length === 0) {
      return (
        <Alert
          message="No list matches the selected filters. Please adjust the filters."
          type="warning"
          showIcon
        />
      );
    }
  }, [displayedData, entityListType, entityLists, isFetching, selectedEntityList]);

  return (
    <>
      <Section
        header={getEntityListSpecification(entityListType).title}
        headerActions={headerActions}
        $scrollableContent={true}
        $ratio={0}
        data-testid="EntityListsSection"
        $minWidth="44rem"
        $contentMarginTop={false}
      >
        {entityLists && (
          <EntityListsFilters
            entityListType={entityListType}
            entityListsSummary={getEntityListsSummary(entityLists, showArchivedItems)}
          />
        )}
        {listContent}
      </Section>
      {modalOpened && (
        <EntityListModal
          entityListType={entityListType}
          onClose={onCreateEntityListModalClose}
          confirmButtonLabel="Create"
        />
      )}
    </>
  );
};

export default EntityListCatalog;
